import React, { useEffect, useRef, useState } from "react";
import { Card, Dropdown, Popover, Tooltip } from "antd";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { Add, Apartment, MoreVert, DragIndicator } from "@mui/icons-material";
import AddColumnModal from "./add-column";
import { KanbanSkeleton } from "../../../../../components/page-skeleton";
import DeleteColumnModal from "./delete-column";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedCompany } from "../../../../../store/actions/company";
import { useHistory } from "react-router-dom";

function DraggableCompanyCard({ company, userCompanies }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `company-${company.id}`,
  });

  const navigateToCompany = () => {
    const selectedCompany = userCompanies.find((c) => c.id === company.id);
    dispatch(setSelectedCompany(selectedCompany));
    history.push(`/company-tasks`);
  };

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const content = (
    <>
      <h2>Company Tasks</h2>
      {company.boards?.length >= 1 ? (
        <ul>
          {company.boards?.map((item) => (
            <li key={item.id}>
              <b>{item.name}:</b> {item.task_count} task{item.status !== 1 && "s"}
            </li>
          ))}
        </ul>
      ) : (
        <p className="no-data-available">No status available</p>
      )}
    </>
  );

  return (
    <Popover overlayClassName="company-popover-content" content={content} trigger="hover">
      <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
        <div className="draggable-status" onClick={navigateToCompany}>
          {company.shared && <span className="tagline">Shared</span>}
          <div className="thumb">
            <Apartment />
          </div>
          <h4>{company.name}</h4>
        </div>
      </div>
    </Popover>
  );
}

function DraggableStatusColumn({ status, children }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `status-${status.id}`,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 99,
        border: "1px solid #f0f0f0",
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="draggable-status-cards"
    >
      {children}
    </div>
  );
}

function DroppableCell({ id, children }) {
  const { setNodeRef } = useDroppable({
    id: id,
  });

  return (
    <div ref={setNodeRef} className="dropable-cell">
      {children}
    </div>
  );
}

function Pipeline() {
  // STATES
  const [isLoading, setLoading] = useState(true);
  const [addPreview, setaddPreview] = useState(false);
  const [deletePreview, setDeletePreview] = useState(false);
  const [activeCompany, setActiveCompany] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [companies, setCompanies] = useState([]);

  // REDUX DATA
  const userCompanies = useSelector((state) => state.CompanyReducer.companies);

  // CONST VALS
  const { http } = global.services;
  const editInputRef = useRef(null);
  const sortedCompanies = companies.sort((a, b) => a.row - b.row);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // HELPERS
  const getCompaniesStatusHelper = () => {
    http
      .get("company-status")
      .finally(() => setLoading(false))
      .then(({ data }) => {
        if (!Array.isArray(data)) return;

        const assignedRows = new Set(data.map((item) => item.row));
        let highestRow = data.length > 0 ? Math.max(...assignedRows) : 0;

        const companies = userCompanies.map((company) => {
          const status = data.find((s) => s.company_id === company.id);

          let row;
          if (status) {
            row = status.row;
          } else {
            while (assignedRows.has(highestRow + 1)) {
              highestRow++;
            }
            row = highestRow + 1;
            assignedRows.add(row);
          }

          return {
            id: company.id,
            name: company.company_name,
            boards: company.boards,
            shared: !company.created_by_user,
            row,
            status: status ? status.status : 0,
          };
        });
        setCompanies(companies);
      })
      .catch(() => {});
  };

  const getColumnsHelper = () => {
    setLoading(true);
    http
      .get("pipeline")
      .then(({ data }) => {
        setStatuses(data.sort((a, b) => a.order - b.order));
        getCompaniesStatusHelper();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // HANDLERS
  const addPreviewHandler = () => setaddPreview(!addPreview);

  const deletePreviewHandler = () => setDeletePreview(!deletePreview);

  const editColumnPreviewHandler = (item) => {
    setSelectedColumn(item);
    setEditInputValue(item.name);
    setEditInputIndex(item.id);
  };

  const updateColumnHandler = () => {
    const oldVal = statuses.find((x) => x.id === editInputIndex).name;
    setEditInputIndex(-1);
    if (editInputValue === oldVal || editInputValue === "") return;
    http
      .put(`pipeline/${selectedColumn.id}`, { name: editInputValue })
      .then(() => {
        setEditInputValue("");
        setSelectedColumn(null);
        setStatuses(
          statuses.map((x) =>
            x.id === editInputIndex ? { ...x, name: editInputValue } : x
          )
        );
      })
      .catch(() => {});
  };

  const openDeleteModal = (column) => {
    setSelectedColumn(column);
    deletePreviewHandler();
  };

  const addSuccessHandler = (status) => {
    setStatuses([...statuses, status]);
  };

  const deleteSuccessHandler = (status) => {
    setStatuses(statuses.filter((s) => s.id !== status));
    companies.forEach((company) => {
      if (company.status === status) {
        setCompanies(
          companies.map((c) => (c.status === status ? { ...c, status: 0 } : c))
        );
      }
    });
  };

  const handleDragStart = (event) => {
    const { active } = event;
    if (active.id.startsWith("company-")) {
      const companyId = active.id.split("-")[1];
      const company = companies.find((c) => c.id === Number.parseInt(companyId));
      if (company) setActiveCompany(company);
    } else if (active.id.startsWith("status-")) {
      const statusId = active.id.split("-")[1];
      const status = statuses.find((s) => s.id === Number.parseInt(statusId));
      if (status) setActiveStatus(status);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id.startsWith("company-")) {
      handleCompanyDragEnd(active, over);
    } else if (active.id.startsWith("status-")) {
      handleStatusDragEnd(active, over);
    }

    setActiveCompany(null);
    setActiveStatus(null);
  };

  const handleCompanyDragEnd = (active, over) => {
    const companyId = active.id.split("-")[1];
    const activeCompany = companies.find((c) => c.id === Number.parseInt(companyId));
    const overPosition = JSON.parse(over.id);

    if (!activeCompany) return;

    const newCompanies = [...companies];

    // Find company in target position (if any)
    const swapCompany = newCompanies.find(
      (c) => c.row === overPosition.row && c.id !== activeCompany.id
    );

    // Update active company position
    const activeIndex = newCompanies.findIndex((c) => c.id === activeCompany.id);
    newCompanies[activeIndex] = {
      ...activeCompany,
      row: overPosition.row,
      status: overPosition.status,
    };

    if (swapCompany) {
      const swapIndex = newCompanies.findIndex((c) => c.id === swapCompany.id);
      newCompanies[swapIndex] = {
        ...swapCompany,
        row: activeCompany.row,
      };
    }
    setCompanies(newCompanies);
    http.put("company-status", { companies: newCompanies }).catch(() => {});
  };

  const handleStatusDragEnd = (active, over) => {
    const statusId = active.id.split("-")[1];
    const newStatuses = [...statuses];
    const oldIndex = newStatuses.findIndex((s) => s.id === Number.parseInt(statusId));
    const newIndex = newStatuses.findIndex((s) => `status-${s.id}` === over.id);

    if (oldIndex !== newIndex) {
      const [reorderedStatus] = newStatuses.splice(oldIndex, 1);
      newStatuses.splice(newIndex, 0, reorderedStatus);
      setStatuses(newStatuses);
      http
        .patch("pipeline", {
          new_order: newStatuses.map((status, index) => {
            return { id: status.id, order: index + 1 };
          }),
        })
        .catch(() => {});
    }
  };

  // HOOKS
  useEffect(() => {
    if (userCompanies.length < 1) return;
    getColumnsHelper();
  }, [userCompanies]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputIndex]);

  return (
    <Card className="pipeline-wrapper">
      <h2>Companies Pipeline</h2>{" "}
      {isLoading ? (
        <KanbanSkeleton />
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="pipelines">
            <div className="company-list listing-card">
              <div className="title-col">Companies</div>
              {sortedCompanies.map((c) => {
                const row = c.row;
                const position = { row: row, status: 0 };
                const company = companies.find((c) => c.row === row && c.status === 0);

                return (
                  <div key={row} className="status-col">
                    <DroppableCell id={JSON.stringify(position)}>
                      {company && (
                        <DraggableCompanyCard
                          company={company}
                          userCompanies={userCompanies}
                        />
                      )}
                    </DroppableCell>
                  </div>
                );
              })}
            </div>
            <div className="status-list listing-card">
              <div style={{ display: "flex" }}>
                {statuses.map((status, index) => (
                  <DroppableCell key={status.id} id={`status-${status.id}`}>
                    <DraggableStatusColumn status={status}>
                      <div className="title-col">
                        <DragIndicator className="drag-handle" />
                        {editInputIndex === status.id ? (
                          <input
                            ref={editInputRef}
                            value={editInputValue}
                            onChange={(e) => setEditInputValue(e.target.value)}
                            onBlur={updateColumnHandler}
                            onKeyDown={(e) => e.key === "Enter" && updateColumnHandler()}
                          />
                        ) : (
                          <span onDoubleClick={() => editColumnPreviewHandler(status)}>
                            {status.name.length > 13 ? (
                              <Tooltip title={status.name}>
                                {status.name.slice(0, 13) + "..."}
                              </Tooltip>
                            ) : (
                              status.name
                            )}
                          </span>
                        )}
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: "0",
                                onClick: () => editColumnPreviewHandler(status),
                                label: <span>Rename</span>,
                              },
                              {
                                type: "divider",
                              },
                              {
                                key: "1",
                                onClick: () => openDeleteModal(status),
                                label: <span>Delete</span>,
                              },
                            ],
                          }}
                          trigger={["click"]}
                          overlayClassName="status-action-dropdown"
                          getPopupContainer={(trigger) => trigger.parentNode}
                        >
                          <MoreVert className="action-btn" />
                        </Dropdown>
                      </div>
                      {sortedCompanies.map((c) => {
                        const row = c.row;
                        const position = { row, status: status.id };
                        const company = companies.find(
                          (c) => c.row === row && c.status === status.id
                        );
                        const companyStatusIndex = statuses.findIndex((s) =>
                          companies.some((c) => c.row === row && c.status === s.id)
                        );
                        return (
                          <div
                            key={row}
                            className={`status-col ${
                              index !== statuses.length - 1 ? "spacing" : ""
                            } ${index === 0 ? "in-progress" : ""} ${
                              companyStatusIndex === index
                                ? "current"
                                : companyStatusIndex > index
                                ? "completed"
                                : companyStatusIndex < index
                                ? "remaining"
                                : ""
                            }`}
                          >
                            <DroppableCell id={JSON.stringify(position)}>
                              {company && (
                                <DraggableCompanyCard
                                  company={company}
                                  userCompanies={userCompanies}
                                />
                              )}
                            </DroppableCell>
                          </div>
                        );
                      })}
                    </DraggableStatusColumn>
                  </DroppableCell>
                ))}
                <div
                  className={`add-status-card ${
                    statuses.length === 0 ? "stretched" : ""
                  }`}
                  style={{ minHeight: companies.length * 80 + 58 }}
                >
                  <Tooltip title="Add status column">
                    <button onClick={addPreviewHandler}>
                      <Add />
                    </button>
                  </Tooltip>
                  {/* {statuses.length === 0 && <p>Add status column</p>} */}
                </div>
              </div>
            </div>
          </div>
          <DragOverlay>
            {activeCompany && (
              <DraggableCompanyCard
                company={activeCompany}
                userCompanies={userCompanies}
              />
            )}
            {activeStatus && (
              <div className="draggable-status-cards">
                <div className="title-col">
                  <span>{activeStatus.name}</span>
                </div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
      <AddColumnModal
        preview={addPreview}
        previewHandler={addPreviewHandler}
        successHandler={addSuccessHandler}
      />
      <DeleteColumnModal
        preview={deletePreview}
        previewHandler={deletePreviewHandler}
        selectedColumn={selectedColumn}
        successHandler={deleteSuccessHandler}
      />
    </Card>
  );
}

export default Pipeline;
